// ゲーム機の中に出す、マイピース / みんなのピースを選ぶカルーセル。
// 寸法はアルゴリズムの StageList / AlgorithmStagePage と同じ。カードは傾けず、横に動かして選んだものを大きくするだけ。
// 選ぶと全画面の一覧（/courses/builder）へ進む

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "app/store";
import I18n from "common/i18n";
import Constants from "common/constant";
import { btnDelay, usePlaySound } from "common/utils";
import { IconArrowLeft, IconArrowRight } from "common/assets";
import { actions } from "features/builder/slice";
import { WoodenBg } from "features/courses/assets";
import { PieceListContext } from "features/builder/pieceList";
import {
  PillSize,
  PillColor,
  BuilderPillButton,
} from "features/builder/elements/BuilderPillButton";

const ENTRIES: {
  tab: PieceListContext;
  labelKey: string;
  descKey: string;
  icon: string;
  image: string;
  accentBg: string;
}[] = [
  {
    tab: PieceListContext.MINE,
    labelKey: "MSG_BUILDER_TAB_MY_MAPS",
    descKey: "MSG_BUILDER_ENTRY_MINE_DESC",
    icon: "construction",
    image: "course_builder_card_mine",
    accentBg: "bg-orange-100",
  },
  {
    tab: PieceListContext.PUBLIC,
    labelKey: "MSG_BUILDER_TAB_PUBLIC_MAPS",
    descKey: "MSG_BUILDER_ENTRY_PUBLIC_DESC",
    icon: "groups",
    image: "course_builder_card_public",
    accentBg: "bg-blue-100",
  },
];

const ITEM_WIDTH = 552;
const ITEM_HEIGHT = 360;
const ITEM_MARGIN = -180;
const ITEM_SCALE = 0.5;
const CENTER_ITEM_MARGIN_LEFT = -ITEM_MARGIN * ITEM_SCALE;
const CENTER_ITEM_MARGIN_RIGHT = -ITEM_MARGIN * 1.3 * ITEM_SCALE;

const EntryCard = ({ index, selected }: { index: number; selected: boolean }) => {
  const entry = ENTRIES[index];

  return (
    <div
      // 角丸は中身（rounded-xl = 12）を ITEM_SCALE で縮めた値
      className="flex-col-el shrink-0 rounded-md"
      style={{
        width: ITEM_WIDTH * ITEM_SCALE,
        height: ITEM_HEIGHT * ITEM_SCALE,
        boxShadow: selected && "0px 0px 6px 1px rgba(63, 74, 97, 0.3)",
      }}
    >
      <div
        className="flex-col-view origin-top-left border-solid border-4 border-textcolor-black rounded-xl overflow-hidden"
        style={{ width: ITEM_WIDTH, height: ITEM_HEIGHT, transform: `scale(${ITEM_SCALE})` }}
      >
        <img
          alt={I18n.t(entry.labelKey)}
          src={`${Constants.assetHost}/assets/images/${entry.image}.webp`}
          className="w-full h-full absolute inset-0 z-[-2] object-cover"
        />

        <div className={`flex-col-center w-4 h-full !absolute top-0 left-0 z-50 ${entry.accentBg}`}>
          {/* 幅 16px の帯の中に absolute で置くので、w-max で中身の幅にする（無いと帯の幅に潰れて文字がはみ出す） */}
          <div className="flex-row-view items-center gap-3 w-max h-12 pr-4 bg-white/80 border-solid border border-l-0 border-white rounded-r-lg !absolute top-6 left-4 whitespace-nowrap">
            <div className={`flex-col-center shrink-0 w-12 h-12 rounded-br-xl ${entry.accentBg}`}>
              <span className="material-symbols-outlined type-title text-white">{entry.icon}</span>
            </div>
            <p className="type-text type-md shrink-0 text-textcolor-black">
              {I18n.t(entry.labelKey)}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export const PieceBuilderSelectPage = () => {
  const play = usePlaySound();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const activeTab = useSelector((state: RootState) => state.builder.listUi.activeTab);
  const [selectedIndex, setSelectedIndex] = useState(
    Math.max(
      ENTRIES.findIndex((e) => e.tab === activeTab),
      0,
    ),
  );
  const [offsetX, setOffsetX] = useState((ITEM_WIDTH + ITEM_MARGIN) * ITEM_SCALE * selectedIndex);
  const selected = ENTRIES[selectedIndex];

  const select = (index: number) => {
    setSelectedIndex(index);
    setOffsetX((ITEM_WIDTH + ITEM_MARGIN) * ITEM_SCALE * index);
  };

  const handleMove = (step: number) => () => {
    play();
    const next = selectedIndex + step;
    if (next >= 0 && next < ENTRIES.length) select(next);
  };

  const handleOpen = () => {
    play();
    btnDelay(() => {
      dispatch(actions.setActiveListTab(selected.tab));
      navigate("/courses/builder");
    });
  };

  const handleTapCard = (index: number) => () => {
    if (index === selectedIndex) {
      handleOpen();
    } else {
      play();
      select(index);
    }
  };

  return (
    <div className="flex-col-el relative w-full h-full bg-[#F5FCFF]">
      <img
        alt="ゲームの背景"
        src={`${Constants.assetHost}/assets/images/bg_scene.jpg`}
        className="absolute top-0 left-0 object-cover opacity-70"
      />

      <div className="flex-row-view w-full h-[430px]">
        <div className="flex-row-view z-1">
          <div className="flex-row-el flex-center upsilon absolute bottom-5 left-3 pointer-events-auto cursor-pointer z-50">
            <button className="btn btn-primary w-[40px] h-[72px]" onClick={handleMove(-1)}>
              <div className="flex-col-center">
                <IconArrowLeft />
              </div>
            </button>
          </div>

          <div className="flex-row-el flex-center upsilon absolute bottom-5 right-3 pointer-events-auto cursor-pointer z-50">
            <button className="btn btn-primary w-[40px] h-[72px]" onClick={handleMove(1)}>
              <div className="flex-col-center">
                <IconArrowRight />
              </div>
            </button>
          </div>

          <div className="flex-row-view w-[1100px] h-full pointer-events-auto">
            <div
              className="flex-row-view items-center w-full h-full pt-3 pointer-events-auto"
              style={{
                transform: `translateX(${-offsetX}px)`,
                transition: "transform 300ms ease-in-out",
              }}
            >
              <div className="flex-row-view w-[330px] h-full" />
              {ENTRIES.map((entry, index) => (
                <div
                  key={entry.tab}
                  className={`flex-col-view cursor-pointer ${
                    selectedIndex === index ? "scale-[2]" : "scale-100"
                  }`}
                  style={{
                    transition: "transform 300ms ease-in-out",
                    zIndex: selectedIndex === index ? 10 : 1,
                    // 真ん中のカードの外枠は余白込みで横のカードにかぶさる。外枠はタップを通し、
                    // タップはカード本体（pointer-events-auto）だけで受ける（StageList と同じ）
                    pointerEvents: selectedIndex === index ? "none" : "auto",
                  }}
                >
                  <div
                    onPointerDown={handleTapCard(index)}
                    className="flex-col-center pointer-events-auto cursor-pointer"
                    style={{
                      marginLeft: selectedIndex === index ? CENTER_ITEM_MARGIN_LEFT : 0,
                      marginRight:
                        selectedIndex === index
                          ? CENTER_ITEM_MARGIN_RIGHT
                          : ITEM_MARGIN * ITEM_SCALE,
                    }}
                  >
                    {selectedIndex !== index && (
                      <div className="flex-col-center !absolute top-0 left-0 w-full h-full bg-textcolor-black/20 z-10" />
                    )}
                    <EntryCard index={index} selected={selectedIndex === index} />
                  </div>
                </div>
              ))}
              <div className="flex-row-view w-[450px] h-full" />
            </div>
          </div>
        </div>
      </div>

      <div className="flex-row-el flex-center w-full absolute bottom-3">
        <div className="flex-row-el flex-center relative w-[848px] h-[98px]">
          <div className="flex-col-el flex-center absolute">
            <WoodenBg />
          </div>

          <div className="flex-row-el flex-center gap-3 absolute -top-8 left-10 pr-6 z-50 bg-white border-solid border-4 border-textcolor-black rounded-lg overflow-hidden">
            <div className={`flex-col-center w-12 h-12 ${selected.accentBg}`}>
              <span className="material-symbols-outlined type-title text-white">
                {selected.icon}
              </span>
            </div>
            <p className="type-text type-title text-textcolor-black">{I18n.t(selected.labelKey)}</p>
          </div>

          <div className="flex-row-el flex-center relative gap-6 mt-2">
            <div className="flex-row-el items-center relative w-[520px] h-[56px]">
              {/* type-text-left は flex-shrink: 0 なので、w-full で枠の幅に固定して折り返させる。
                  break-keep で、ことばの途中（ひらがなの分かち書きの途中）では折り返さない */}
              <p className="type-text-left type-sm w-full break-keep text-white">
                {I18n.t(selected.descKey)}
              </p>
            </div>

            {/* あそぶ画面のスタートボタンと同じ形 */}
            <BuilderPillButton
              color={PillColor.GREEN}
              size={PillSize.MD}
              icon="play_arrow"
              label={I18n.t("MSG_BUILDER_ENTRY_OPEN_BTN")}
              onClick={handleOpen}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
